import {
  aqBootstrapTheme,
  BSCol,
  BSGrid,
  Card,
  Datepicker,
  ErrorMessage,
  Field,
  FieldProps,
  FormikField,
  FormLabel
} from "@appquality/appquality-design-system";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n";
import { getLanguageMessage } from "src/pages/BugForm/BugDetails/getLanguageMessage";
import { BugReplicability } from "src/pages/BugForm/BugDetails/select/BugReplicability";
import { BugSeverity } from "src/pages/BugForm/BugDetails/select/BugSeverity";
import { BugType } from "src/pages/BugForm/BugDetails/select/BugType";
import { Devices } from "src/pages/BugForm/BugDetails/select/Devices";
import { UseCase } from "src/pages/BugForm/BugDetails/select/UseCase";
import { LabelWithHelper } from "src/pages/BugForm/LabelWithHelper/LabelWithHelper";
import useCampaignData from "src/pages/BugForm/useCampaignData";
import { useAppSelector } from "src/store";
import { MarkdownEditor } from "../MarkdownEditor";

interface BugDetailsProps {
  className?: string;
}

export const BugDetails = ({ className }: BugDetailsProps) => {
  const { t } = useTranslation();
  const { data } = useCampaignData();
  const isDesktop = window.matchMedia(
    `only screen and (min-width: ${aqBootstrapTheme.grid.breakpoints.lg})`
  ).matches;

  const isPublicPage = useAppSelector(
    (state) => state.publicUserPages.isPublic
  );

  const lastBugId = useAppSelector((state) => state.bugForm.lastId);

  return (
    <Card
      className={className}
      title={
        <LabelWithHelper
          label={t("BUGFORM_BUGDTLS_TITLE", { defaultValue: "Bug Details" })}
          title={t("BUGTITLEDETAILS_TOOLTIP_TXT", {
            defaultValue: "Learn more",
          })}
          href={t("Bug details help article", {
            ns: "links",
          })}
          hideHelper={isPublicPage}
        />
      }
    >
      {getLanguageMessage(data?.language)}
      <Field
        name="title"
        type="text"
        label={t("BUGFORM_BUGDTLS_BUGTITLE", { defaultValue: "Bug title" })}
        placeholder={t("BUGFORM_BUGDTLS_BUGTITLE_PLACEHOLDER", {
          defaultValue: "[Phase / Section] - Briefly Issue description",
        })}
      />
      {!isPublicPage && (
        <div className="aq-mb-3">
          <Devices />
        </div>
      )}
      <div className="aq-mb-3">
        <BugType />
      </div>
      <div className="aq-mb-3">
        <BugSeverity />
      </div>
      <div className="aq-mb-3">
        <BugReplicability />
      </div>
      {!isPublicPage && (
        <div className="aq-mb-3">
          <UseCase />
        </div>
      )}
      <FormikField name="stepDescription">
        {({ form, field }: FieldProps) => (
          <div className="aq-mb-3">
            <FormLabel
              htmlFor={field.name}
              label={t("BUGFORM_BUGDTLS_STEPBYSTEP", {
                defaultValue: "Step-by-step description",
              })}
            />
            <MarkdownEditor
              key={lastBugId}
              initialValue={field.value}
              onChange={(value) => form.setFieldValue(field.name, value)}
              placeholder={t("BUGFORM_BUGDTLS_STEPBYSTEP_PLACEHOLDER", {
                defaultValue: "1. ​\n2. ​\n3. ​\n",
              })}
            />
            <ErrorMessage name={field.name} />
          </div>
        )}
      </FormikField>
      <FormikField name="expected">
        {({ form, field }: FieldProps) => (
          <div className="aq-mb-3">
            <FormLabel
              htmlFor={field.name}
              label={t("BUGFORM_BUGDTLS_EXPDRES", {
                defaultValue: "Expected result",
              })}
            />
            <MarkdownEditor
              key={lastBugId}
              initialValue={field.value}
              onChange={(value) => form.setFieldValue(field.name, value)}
              placeholder={t("BUGFORM_BUGDTLS_EXPDRES_PLACEHOLDER", {
                defaultValue: "Write what you expected",
              })}
            />
            <ErrorMessage name={field.name} />
          </div>
        )}
      </FormikField>
      <FormikField name="current">
        {({ form, field }: FieldProps) => (
          <div className="aq-mb-3">
            <FormLabel
              htmlFor={field.name}
              label={t("BUGFORM_BUGDTLS_OSBRVDRES", {
                defaultValue: "Observed result",
              })}
            />
            <MarkdownEditor
              initialValue={field.value}
              key={lastBugId}
              onChange={(value) => form.setFieldValue(field.name, value)}
              placeholder={t("BUGFORM_BUGDTLS_OSBRVDRES_PLACEHOLDER", {
                defaultValue: "Write what you found",
              })}
            />
            <ErrorMessage name={field.name} />
          </div>
        )}
      </FormikField>
      <BSGrid>
        <BSCol size="col-6" className="aq-mb-3">
          <FormikField name="date">
            {({ field, form }: FieldProps) => (
              <>
                <FormLabel
                  htmlFor={"date"}
                  label={t("BUGFORM_BUGDTLS_DAY", {
                    defaultValue: "When did it happen",
                  })}
                />
                <Datepicker
                  key={field.name}
                  value={field.value}
                  id={"date"}
                  // maxDate={new Date()}
                  locale={i18n.language}
                  placeholder={t("BUGFORM_BUGDTLS_DAY_PLACEHOLDER", {
                    defaultValue: "Pick a date",
                  })}
                  setText={t("Set")}
                  cancelText={t("Cancel")}
                  onChange={(v: { value: Date }) => {
                    if (isNaN(v.value.getTime())) return;
                    form.setFieldValue(
                      "date",
                      v.value ? v.value : new Date(),
                      true
                    );
                  }}
                />
              </>
            )}
          </FormikField>
        </BSCol>
        <BSCol size="col-6">
          <FormikField name="time">
            {({ field, form }: FieldProps) => (
              <>
                <FormLabel
                  htmlFor={"time"}
                  label={t("BUGFORM_BUGDTLS_TIME", {
                    defaultValue: "What time did it happen",
                  })}
                />
                <Datepicker
                  key={field.name}
                  value={field.value}
                  id={"time"}
                  locale={"it"}
                  control="time"
                  placeholder={t("BUGFORM_BUGDTLS_TIME_PLACEHOLDER", {
                    defaultValue: "Pick a time",
                  })}
                  setText={t("Set")}
                  cancelText={t("Cancel")}
                  onChange={(v: { value: Date }) => {
                    if (isNaN(v.value.getTime())) return;
                    form.setFieldValue(
                      "time",
                      v.value ? new Date(v.value) : new Date(),
                      true
                    );
                  }}
                />
              </>
            )}
          </FormikField>
        </BSCol>
      </BSGrid>
      <FormikField name="notes">
        {({ form, field }: FieldProps) => (
          <div className="aq-mb-3">
            <FormLabel
              htmlFor={field.name}
              label={t("BUGFORM_BUGDTLS_COMMENTS", {
                defaultValue: "Additional comments",
              })}
            />
            <MarkdownEditor
              key={lastBugId}
              initialValue={field.value}
              onChange={(value) => form.setFieldValue(field.name, value)}
              placeholder={t("BUGFORM_BUGDTLS_COMMENTS_PLACEHOLDER", {
                defaultValue: "Anything else you'd like to add",
              })}
            />
          </div>
        )}
      </FormikField>
    </Card>
  );
};
